import { useState } from "react";
import { Columns2, Factory, Lock, Pencil, Plus, Store } from "lucide-react";
import type { Varianta, ZakazkaDetail } from "@/api/schemas";
import { usePrava } from "@/api/hooks/useAuth";
import { formatCisloBezNul, formatEur } from "@/lib/format";
import {
  NAZOV_ODBORU,
  ODBORY,
  popisOdboru,
  variantyOdboru,
  type OdborZakazky,
} from "@/lib/odboryZakazky";
import { VETA, vetaZamku, type RezimVarianty } from "@/lib/prava";
import { jeTovarovaVarianta } from "@/lib/variantyZakazky";
import {
  PodmienkyPonukyDialog,
  type OdborPonuky,
} from "@/components/zakazka/PodmienkyPonukyDialog";
import { cn } from "@/lib/utils";

/**
 * Varianty v cene zákazky po odboroch ponuky — nábytok (aktívny výpočet,
 * zákazka bez výpočtu všetky časti výroby) a aktívny tovar. To isté
 * pravidlo ako `casti_zakazky.varianty_ceny` na serveri (KOLO 231).
 */
export function odboryPonuky(zakazka: ZakazkaDetail): OdborPonuky[] {
  const varianty = (zakazka.varianty ?? []) as Varianta[];
  const nabytok = varianty.filter((v) => !jeTovarovaVarianta(v));
  const vNabytku = zakazka.cena_z_vyroby
    ? nabytok.filter((v) => v.je_vyrobna)
    : nabytok.filter((v) => v.id === zakazka.aktivna_varianta_id);
  const tovar = varianty.find((v) => v.id === zakazka.aktivna_tovar_varianta_id);
  const odbory: OdborPonuky[] = [];
  if (vNabytku.length > 0) odbory.push({ kluc: "nabytok", nazov: "Nábytok", varianty: vNabytku });
  if (tovar) odbory.push({ kluc: "tovar", nazov: "Tovar", varianty: [tovar] });
  return odbory;
}

/** „×1,1 −5 %" — koeficient a zľava ponuky odboru. */
function podmienky(v: Varianta | undefined): string {
  const koef = formatCisloBezNul(v?.koef_ponuky ?? "1");
  const zlava = Number(v?.sleva_pct ?? 0);
  return `×${koef}${zlava ? ` −${formatCisloBezNul(v?.sleva_pct)} %` : ""}`;
}

const IKONA: Record<OdborZakazky, typeof Store> = {
  nabytok: Columns2,
  tovar: Store,
  vyroba: Factory,
};

const FARBA_IKONY: Record<OdborZakazky, string> = {
  nabytok: "text-tandem-gray-dark",
  tovar: "text-sky-700",
  vyroba: "text-amber-700",
};

const NOVY: Record<OdborZakazky, string> = {
  nabytok: "Výpočet",
  tovar: "Tovar",
  vyroba: "Výroba",
};

/** Cena odboru v lište — verzia v cene; výroba súčet častí. */
function cenaOdboru(zakazka: ZakazkaDetail, odbor: OdborZakazky): string | null | undefined {
  const varianty = (zakazka.varianty ?? []) as Varianta[];
  if (odbor === "vyroba") return zakazka.cena_vyroby_s_dph;
  const id = odbor === "tovar" ? zakazka.aktivna_tovar_varianta_id : zakazka.aktivna_varianta_id;
  return varianty.find((v) => v.id === id)?.cena_s_dph;
}

/**
 * Lišta odborov v hlavičke zákazky (KOLO 240, návrh H1b) — jeden pás
 * **Nábytok · Tovar · Výroba** s cenou a počtom verzií, na konci **Ponuka
 * s DPH** s koeficientom a zľavou (do KOLA 239 pás „Rozpočet → Ponuka").
 *
 * Odbor bez verzie je sivý a s právom ho „+" založí. Odbor, ktorý
 * prihlásený len číta (konštruktér výpočty a tovar, rozpočtár tovar),
 * má zámok s dôvodom — otvoriť a prečítať ho môže.
 */
export function ListaOdborov({
  zakazka,
  odbor,
  onOdbor,
  novy,
  rezimy,
}: {
  zakazka: ZakazkaDetail;
  /** Odbor otvorenej verzie. */
  odbor: OdborZakazky | null;
  onOdbor: (odbor: OdborZakazky) => void;
  /** Založenie prvej verzie v prázdnom odbore — bez práva chýba. */
  novy: Partial<Record<OdborZakazky, () => void>>;
  /** Režim práv v odbore (`citanie` / `len_ceny` = zámok s vetou). */
  rezimy: Record<OdborZakazky, RezimVarianty>;
}): JSX.Element {
  const prava = usePrava();
  const [podmienkyOtvorene, setPodmienkyOtvorene] = useState(false);
  const varianty = (zakazka.varianty ?? []) as Varianta[];
  const ponukaOdbory = odboryPonuky(zakazka);
  const rozpocet = zakazka.cena_rozpoctu_s_dph;
  const ponuka = zakazka.cena_celkom_s_dph;
  const lisiSa = rozpocet != null && ponuka != null && Number(rozpocet) !== Number(ponuka);

  return (
    <div
      className="flex h-11 shrink-0 items-stretch gap-0.5 overflow-x-auto rounded-full border bg-background-alt p-[3px]"
      role="tablist"
      aria-label="Odbory zákazky"
    >
      {ODBORY.map((o) => {
        const verzie = variantyOdboru(varianty, o);
        const Ikona = IKONA[o];
        const akt = odbor === o;
        const rezim = rezimy[o];
        const zamok = rezim === "plny" ? null : vetaZamku(rezim, o === "tovar", o === "vyroba");
        if (verzie.length === 0) {
          const zalozit = novy[o];
          return (
            <button
              key={o}
              type="button"
              role="tab"
              aria-selected={false}
              disabled={!zalozit}
              onClick={zalozit}
              className="flex shrink-0 items-center gap-2 rounded-full px-4 font-ui text-sm font-semibold text-muted-foreground hover:bg-white/70 disabled:cursor-default disabled:opacity-60 disabled:hover:bg-transparent"
              title={zalozit ? `Založiť ${NOVY[o].toLowerCase()}` : `${NAZOV_ODBORU[o]} — zatiaľ nič`}
            >
              <Ikona className="h-4 w-4" aria-hidden />
              {NAZOV_ODBORU[o]}
              {zalozit ? (
                <span className="flex items-center gap-0.5 text-xs font-medium">
                  <Plus className="h-3.5 w-3.5" aria-hidden />
                  pridať
                </span>
              ) : (
                <span className="text-xs font-medium">—</span>
              )}
            </button>
          );
        }
        const bezVypoctu = o === "nabytok" && zakazka.cena_z_vyroby;
        return (
          <button
            key={o}
            type="button"
            role="tab"
            aria-selected={akt}
            onClick={() => onOdbor(o)}
            className={cn(
              "flex shrink-0 items-center gap-2 rounded-full px-4 font-ui text-sm font-semibold whitespace-nowrap transition-colors",
              akt
                ? "bg-white text-tandem-red-dark shadow-sm"
                : "text-tandem-gray-dark hover:bg-white/70",
            )}
            title={zamok ?? undefined}
          >
            <Ikona className={cn("h-4 w-4", FARBA_IKONY[o])} aria-hidden />
            {NAZOV_ODBORU[o]}
            <span className="font-display tabular-nums text-foreground" data-testid={`cena-odboru-${o}`}>
              {bezVypoctu ? "bez výpočtu" : formatEur(cenaOdboru(zakazka, o))}
            </span>
            <span className="text-xs font-medium text-muted-foreground">
              {popisOdboru(o, verzie.length)}
            </span>
            {zamok ? (
              <Lock className="h-3 w-3 text-muted-foreground" aria-label={zamok} />
            ) : null}
          </button>
        );
      })}

      {ponukaOdbory.length > 0 ? (
        <div
          className="ml-auto flex shrink-0 items-center gap-2 border-l pl-4 pr-1.5 whitespace-nowrap"
          data-testid="ponuka-zakazky"
          title={
            lisiSa
              ? `Rozpočet ${formatEur(rozpocet)} → ponuka ${formatEur(ponuka)}`
              : prava.podmienky
                ? undefined
                : VETA.podmienky
          }
        >
          <span className="text-[10px] font-extrabold uppercase tracking-wider text-muted-foreground">
            Ponuka s DPH
          </span>
          <b
            className={cn("font-display text-base tabular-nums", lisiSa ? "text-primary" : "text-foreground")}
            data-testid="ponuka-cena"
          >
            {formatEur(ponuka)}
          </b>
          <span className="text-xs text-muted-foreground">
            {ponukaOdbory
              .map((o) => (ponukaOdbory.length > 1 ? `${o.nazov.toLowerCase()} ` : "") + podmienky(o.varianty[0]))
              .join(" · ")}
          </span>
          {prava.podmienky ? (
            <button
              type="button"
              onClick={() => setPodmienkyOtvorene(true)}
              className="rounded-full p-1.5 text-muted-foreground hover:bg-white hover:text-foreground"
              aria-label="Upraviť podmienky ponuky"
              title="Koeficient a zľava ponuky pre nábytok a tovar"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>
      ) : null}
      {podmienkyOtvorene ? (
        <PodmienkyPonukyDialog
          zakazkaId={zakazka.id}
          odbory={ponukaOdbory}
          open
          onOpenChange={setPodmienkyOtvorene}
        />
      ) : null}
    </div>
  );
}
